import React from 'react';
import { Heart, DollarSign, Phone, Award } from 'lucide-react';
import { motion } from 'framer-motion';

const MedicalServices: React.FC = () => {
  const medicalNeeds = [
    'Medication and supplies to treat heartworm for dogs',
    'Medical care for a stray mama cat and kittens or mom and litter of puppies',
    'Repair of a broken leg for a stray pup or cat that was hit by a car',
    'Help regulate diabetes medication for an elderly cat/dog',
    'Repair a torn CCL for a young, active and healthy dog',
    'A pet requiring specialty or extensive care to become adoptable',
    'Dental procedures or tooth extractions',
  ];

  return (
    <div className="min-h-screen bg-cream-white">
      <section className="bg-gradient-to-r from-warm-coral to-sky-blue py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-5xl text-white mb-4"
          >
            Medical Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/90 text-xl max-w-3xl mx-auto"
          >
            Lovebug's Legacy provides crucial funding for pets with special medical needs
          </motion.p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl p-8 shadow-md mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Heart className="text-warm-coral" size={32} fill="currentColor" />
            <h2 className="font-display text-3xl text-deep-forest">Lovebug's Legacy</h2>
          </div>
          
          <p className="text-warm-gray mb-6 leading-relaxed">
            On the evening of November 11th 2023 an emaciated dog came through our doors. This was the worst case 
            of animal cruelty our staff had ever seen. Despite community support that raised over $14,000, 
            Lovebug passed away. The fund now helps other animals with special medical needs.
          </p>

          <div className="bg-warm-coral/10 rounded-xl p-6 mb-8">
            <h3 className="font-semibold text-xl text-deep-forest mb-4">What Lovebug's Legacy Funds:</h3>
            <ul className="space-y-3">
              {medicalNeeds.map((need, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-warm-coral mt-1">•</span>
                  <span className="text-warm-gray">{need}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-sky-blue/10 rounded-xl p-6 text-center">
              <Award className="text-sky-blue mx-auto mb-3" size={32} />
              <h4 className="font-semibold text-lg text-deep-forest mb-2">Success Stories</h4>
              <p className="text-warm-gray">Popo, Dozer, and Ruben are just a few of the many pets helped</p>
            </div>
            <div className="bg-sage-green/10 rounded-xl p-6 text-center">
              <DollarSign className="text-sage-green mx-auto mb-3" size={32} />
              <h4 className="font-semibold text-lg text-deep-forest mb-2">Make a Difference</h4>
              <p className="text-warm-gray">Your donation directly helps pets with medical needs</p>
            </div>
            <div className="bg-soft-lavender/20 rounded-xl p-6 text-center">
              <Phone className="text-soft-lavender mx-auto mb-3" size={32} />
              <h4 className="font-semibold text-lg text-deep-forest mb-2">Contact Us</h4>
              <p className="text-warm-gray">Call 218-722-5341 to learn more</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalServices;